import Phaser from "phaser";
import { SfxCue, unlockAudio } from "../audio/sfx";
import { takeRaidEncounterStart, takeRaidParty, raidCatalog } from "../data/raid-catalog";
import { shouldRestartOnQuit } from "../flow/entry";
import { SceneKey, assertTransition } from "../flow/game-flow";
import {
  createBattleState,
  createGestureState,
  drainEvents,
  gestureCancel,
  gestureDown,
  gestureMove,
  gesturePrep,
  gestureTick,
  gestureUp,
  gestureVisual,
  getHudSnapshot,
  hitTestAllies,
  issueGroupDash,
  issueUnitDash,
  setUnitPin,
  startAssaultEncounter,
  tickRaid,
  type GestureEmit,
  type GestureState,
  type RaidBattleState,
  type RaidHudSnapshot,
} from "../features/raid";
import { raidFreezeDiag, raidFreezeNowMs } from "../features/raid/raid-freeze-diag";
import { RaidPresentation } from "../features/raid/view/raid-presentation";
import { sharedInput } from "../input/input-controller";
import { mountRaidHud } from "../ui/huds/raid";
import { getUiLayout } from "../ui/layout";
import { clearOverlay } from "../ui/screen-overlay";
import { markActiveScene } from "../ui/screen-state";

function gestureKnobsFromCatalog() {
  const g = (
    raidCatalog.balance as {
      gesture?: {
        dashMaxDist?: number;
        minSwipePx?: number;
        dragStartPx?: number;
        pinDelaySec?: number;
        areaRMin?: number;
        areaRMax?: number;
        areaGrowPxPerSec?: number;
        unitHitPad?: number;
      };
    }
  ).gesture;
  return {
    dashMaxDist: Number(g?.dashMaxDist) || 220,
    minSwipePx: Number(g?.minSwipePx) || 24,
    dragStartPx: Number(g?.dragStartPx) || 12,
    pinDelaySec: Number(g?.pinDelaySec) || 0.25,
    areaRMin: Number(g?.areaRMin) || 40,
    areaRMax: Number(g?.areaRMax) || 208,
    areaGrowPxPerSec: Number(g?.areaGrowPxPerSec) || 96,
    unitHitPad: Number(g?.unitHitPad) || 24,
  };
}

export class RaidBattleScene extends Phaser.Scene {
  private state!: RaidBattleState;
  private view!: RaidPresentation;
  private hud!: ReturnType<typeof mountRaidHud>;
  private gesture!: GestureState;
  private disposed = false;
  private boundDown = (p: Phaser.Input.Pointer) => this.onPointerDown(p);
  private boundMove = (p: Phaser.Input.Pointer) => this.onPointerMove(p);
  private boundUp = (p: Phaser.Input.Pointer) => this.onPointerUp(p);

  constructor() {
    super(SceneKey.RaidBattle);
  }

  preload(): void {
    const g = (
      raidCatalog.view as {
        stageGround?: { textureKey?: string; path?: string };
      }
    ).stageGround;
    const key = g?.textureKey ?? "raid:stage-ground";
    const path = g?.path;
    if (path && !this.textures.exists(key)) {
      this.load.image(key, path);
    }
  }

  create(): void {
    this.disposed = false;
    markActiveScene(SceneKey.RaidBattle);
    sharedInput.attach();
    clearOverlay();
    getUiLayout().sync();

    this.cameras.main.setBackgroundColor("#0c1014");
    const party = takeRaidParty();
    const startMode = takeRaidEncounterStart();
    this.state = createBattleState(party);
    if (startMode === "assault") {
      startAssaultEncounter(this.state);
    }
    this.view = new RaidPresentation(this);
    this.view.create(this.state);
    this.gesture = createGestureState(gestureKnobsFromCatalog());

    this.hud = mountRaidHud({
      onQuit: () => this.quitToTitle(),
      onRetry: () => this.backToRoster(),
    });
    this.hud.sync(this.hudSnap());

    this.input.on("pointerdown", this.boundDown);
    this.input.on("pointermove", this.boundMove);
    this.input.on("pointerup", this.boundUp);
    this.input.on("pointerupoutside", this.boundUp);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  update(_t: number, dtMs: number): void {
    if (this.disposed) return;
    const frameT0 = raidFreezeNowMs();
    const pressed = sharedInput.consumePressed();
    if (pressed.cancel) {
      this.quitToTitle();
      return;
    }
    const dt = Math.min(0.05, dtMs / 1000);
    try {
      tickRaid(this.state, dt);
    } catch (err) {
      raidFreezeDiag.noteException("RaidBattleScene.tickRaid", err);
      // Keep the frame alive so HUD/console stay usable after a sim throw.
    }

    try {
      if (this.state.phase !== "playing") {
        this.applyGestureEmits(gestureCancel(this.gesture));
      } else {
        this.applyGestureEmits(gestureTick(this.gesture, this.state.time));
      }
      this.view.setGestureVisual(
        gestureVisual(this.gesture, this.state.time),
      );

      const events = drainEvents(this.state);
      this.view.sync(this.state, events);
      this.hud.sync(this.hudSnap());
    } catch (err) {
      raidFreezeDiag.noteException("RaidBattleScene.presentation", err);
    } finally {
      raidFreezeDiag.noteTickDuration(
        "RaidBattleScene.frame",
        raidFreezeNowMs() - frameT0,
        {
          simTime: this.state.time,
          dtMs: Math.round(dtMs),
          wave: this.state.encounter?.waveIndex,
        },
      );
    }
  }

  private hudSnap(): RaidHudSnapshot {
    const base = getHudSnapshot(this.state);
    const prep = gesturePrep(this.gesture);
    return {
      ...base,
      prepKind: prep.kind,
      prepUnitId: prep.unitId,
    };
  }

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    if (this.disposed || this.state.phase !== "playing") return;
    unlockAudio();
    const x = pointer.worldX;
    const y = pointer.worldY;
    const pad =
      Number(
        (raidCatalog.balance as { gesture?: { unitHitPad?: number } }).gesture
          ?.unitHitPad,
      ) || 24;
    const hit = hitTestAllies(x, y, this.state.units, pad);
    gestureDown(this.gesture, x, y, this.state.time, hit);
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    if (this.disposed || this.state.phase !== "playing") return;
    if (!pointer.isDown && this.gesture.mode === "idle") return;
    gestureMove(this.gesture, pointer.worldX, pointer.worldY, this.state.time);
  }

  private onPointerUp(pointer: Phaser.Input.Pointer): void {
    if (this.disposed) return;
    if (this.state.phase !== "playing") {
      this.applyGestureEmits(gestureCancel(this.gesture));
      return;
    }
    this.applyGestureEmits(
      gestureUp(this.gesture, pointer.worldX, pointer.worldY, this.state.time),
    );
  }

  private applyGestureEmits(emits: readonly GestureEmit[]): void {
    for (const e of emits) {
      if (e.type === "dash") {
        if (issueUnitDash(this.state, e.unitId, e.tx, e.ty)) {
          SfxCue.uiCommit();
        }
      } else if (e.type === "groupDash") {
        if (
          issueGroupDash(this.state, e.cx, e.cy, e.radius, e.tx, e.ty) > 0
        ) {
          SfxCue.uiCommit();
        }
      } else if (e.type === "pinStart") {
        if (!setUnitPin(this.state, e.unitId, true)) {
          // Mana block — leave pinning mode without emitting pinEnd (no pin issued).
          this.gesture.mode = "idle";
          this.gesture.pinnedUnitId = -1;
        } else {
          SfxCue.uiCommit();
        }
      } else if (e.type === "pinEnd") {
        setUnitPin(this.state, e.unitId, false);
      }
    }
  }

  private backToRoster(): void {
    assertTransition(SceneKey.RaidBattle, SceneKey.RaidRoster);
    this.scene.start(SceneKey.RaidRoster);
  }

  private quitToTitle(): void {
    if (shouldRestartOnQuit(SceneKey.RaidBattle)) {
      assertTransition(SceneKey.RaidBattle, SceneKey.RaidBattle);
      this.scene.restart();
      return;
    }
    assertTransition(SceneKey.RaidBattle, SceneKey.Title);
    this.scene.start(SceneKey.Title);
  }

  private teardown(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.input.off("pointerdown", this.boundDown);
    this.input.off("pointermove", this.boundMove);
    this.input.off("pointerup", this.boundUp);
    this.input.off("pointerupoutside", this.boundUp);
    if (this.gesture) {
      this.applyGestureEmits(gestureCancel(this.gesture));
    }
    this.hud?.destroy();
    this.view?.destroy();
    getUiLayout().clearShell();
  }
}
